import { FC, Fragment, useState } from 'react';
import { useEventBus } from '../../../../../../hooks/use-event-bus';
import { PatientDto } from '../../../../../../services/forms-service/forms-service-types';
import { PatientProfileHeader } from '../../header/header';
import {
  ButtonsContainer,
  Completed,
  ConsentGiven,
  ConsentNeeded,
  Container,
  FormContainer,
  FormInfo,
  FormTitle,
  Menu,
  MenuItem,
  NotSubmitted,
  PatientInfo
} from './forms.styles';

import CirclePlusIcon from '@mui/icons-material/AddCircleOutline';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmailIcon from '@mui/icons-material/Email';
import UploadFileIcon from '@mui/icons-material/FileUpload';
import SmsIcon from '@mui/icons-material/Forum';
import PhoneIcon from '@mui/icons-material/PhoneIphone';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { Button, Divider } from '@mui/material';
import { getAge, useForms } from '../../../../../../hooks/use-forms-service';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { EventFormsDialog } from '../../../../../../types/event-bus';
import { actDisplayModal } from '../../../forms-widget/modal-select-patient';
import { QuickNavMenu } from '../../header/quick-nav-menu/quick-nav-menu';
import { Action } from './action-component';
import { FormsSkeleton } from './forms.skeleton';

const renderConsent = (patient: PatientDto, publish: (event: any) => void, publishToIframes: (event: any) => void) => {
  if (patient.isResponsibleParty) {
    return null;
  }

  const age = getAge(patient.dateOfBirth);
  if (age && age < 18) {
    return 'Minor';
  }

  if (!patient.consentGiven) {
    return (
      <ConsentNeeded
        onClick={() => {
          actDisplayModal({
            publish,
            publishToIframes,
            patient,
            actionType: 'consent'
          });
        }}
      >
        Consent Needed
      </ConsentNeeded>
    );
  } else {
    return <ConsentGiven>Consent Given</ConsentGiven>;
  }
};

export const FormsView: FC = () => {
  const { publish, publishToIframes } = useEventBus();

  const { patientInfo } = usePatientProfile();

  const [sendFillAnchorEl, setSendFillAnchorEl] = useState<null | HTMLElement>(null);
  const sendFillOpen = Boolean(sendFillAnchorEl);

  const [addRemoveAnchorEl, setAddRemoveAnchorEl] = useState<null | HTMLElement>(null);
  const addRemoveOpen = Boolean(addRemoveAnchorEl);

  const { forms, isLoading, formsService } = useForms(patientInfo.id);

  const onClick = (actionType: EventFormsDialog['data']['type'] | 'upload-form', shouldHaveForms = true) => {
    return () => {
      const patientsToLookInto = shouldHaveForms
        ? forms?.patients.filter((patient) => patient.formAssignments?.length)
        : forms?.patients;

      setSendFillAnchorEl(null);
      setAddRemoveAnchorEl(null);
      if (patientsToLookInto?.length === 1) {
        actDisplayModal({
          publish,
          publishToIframes,
          patient: patientsToLookInto[0],
          actionType
        });
      } else {
        publish({
          messageType: 'formsDialog',
          data: {
            type: 'select-patient',
            actionType,
            patientId: patientInfo.id
          }
        });
      }
    };
  };

  const shouldBeDisabled = forms?.patients?.every((p) => p.formAssignments?.length === 0);

  return (
    <>
      <PatientProfileHeader />
      <QuickNavMenu />
      <Container>
        {isLoading && <FormsSkeleton />}
        {!isLoading &&
          forms?.patients?.map((patient) => (
            <Fragment key={`forms-${patient.id}`}>
              <PatientInfo>
                <b>
                  {patient.firstName} {patient.lastName}, {getAge(patient.dateOfBirth)}
                  {patient.isResponsibleParty && ' (RP)'}
                </b>
                <div>{renderConsent(patient, publish, publishToIframes)}</div>
              </PatientInfo>
              {patient.formAssignments?.map((form) => (
                <FormContainer key={`form-${patient.id}-${form.id}`}>
                  <FormInfo>
                    <div>
                      <b>{form.submittedString || form.assignedString}</b>
                      {' \u2022 '}
                      {form.formType}
                      <FormTitle>{form.formName}</FormTitle>
                      <Action form={form} formsService={formsService} />
                    </div>
                    <div>
                      {form.submitted ? <Completed>Complete</Completed> : <NotSubmitted>Not Submitted</NotSubmitted>}
                    </div>
                  </FormInfo>
                </FormContainer>
              ))}
            </Fragment>
          ))}
      </Container>
      <ButtonsContainer>
        <Button
          variant="contained"
          disableElevation
          onClick={(e) => setSendFillAnchorEl((a) => (a ? null : e.currentTarget))}
          endIcon={<ArrowDropUpIcon />}
        >
          Send/Fill
        </Button>
        <Menu anchorEl={sendFillAnchorEl} open={sendFillOpen} onClose={() => setSendFillAnchorEl(null)}>
          <MenuItem onClick={onClick('fill-on-this-device', false)}>
            <PhoneIcon />
            Fill on this device
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem onClick={onClick('send-email-link', false)}>
            <EmailIcon />
            Send Email Link
          </MenuItem>
          <MenuItem onClick={onClick('send-sms-link', false)}>
            <SmsIcon />
            Send Text Link
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem onClick={onClick('scan-qr-code', false)}>
            <QrCodeIcon />
            Scan QR Code
          </MenuItem>
        </Menu>
        <Button
          variant="contained"
          disableElevation
          onClick={(e) => setAddRemoveAnchorEl((a) => (a ? null : e.currentTarget))}
          endIcon={<ArrowDropUpIcon />}
        >
          Add/Remove
        </Button>
        <Menu anchorEl={addRemoveAnchorEl} open={addRemoveOpen} onClose={() => setAddRemoveAnchorEl(null)}>
          <MenuItem onClick={onClick('add-form', false)}>
            <CirclePlusIcon />
            Add Form From Library
          </MenuItem>
          <MenuItem onClick={onClick('upload-form', false)}>
            <UploadFileIcon />
            Upload Document
          </MenuItem>
          <MenuItem onClick={onClick('remove-all-assigned-forms')} disabled={shouldBeDisabled}>
            <DeleteOutlineIcon />
            Remove All Assigned Forms
          </MenuItem>
        </Menu>
      </ButtonsContainer>
    </>
  );
};
